import { createPortal } from 'react-dom';
import { Component, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { CONFIG } from './config';
import type { ProjectEntry, ResolvedContent } from './content/types';
import { predecode } from './lib/decode';
import { uptimeHours } from './runtime/heartbeat';
import { report } from './runtime/monitor';
import { applyAppUpdate, hasAppUpdate } from './runtime/swUpdate';
import { Attract } from './screens/Attract';
import { defaultTab, Gallery, imageLabel, modeloAbierto, TAB_LABEL, tabItems, verItems } from './screens/Gallery';
import type { Tab } from './screens/Gallery';
import { Home } from './screens/Home';
import { AdminPanel, FirstBoot, IdlePrompt } from './screens/Overlays';
import type { SyncEngine } from './sync/SyncEngine';
import { ContentContext, type ContentCtx } from './ui/ContentContext';
import { openViewer, preloadViewer, type ViewerHandle } from './viewer/viewer';

type Nav = { name: 'attract' } | { name: 'home' } | { name: 'gallery'; projectId: string; tab: Tab; idx: number; modelo: number | null };

let currentScreen = 'boot';
export const getCurrentScreen = () => currentScreen;

export function App({ sync }: { sync: SyncEngine }) {
  const [content, setContent] = useState<ResolvedContent | null>(sync.content);
  const [nav, setNav] = useState<Nav>({ name: 'attract' });
  const [vis, setVis] = useState({ o: 1, y: 0 });
  const [prompt, setPrompt] = useState(false);
  const [admin, setAdmin] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const navRef = useRef(nav);
  const viewer = useRef<ViewerHandle | null>(null);
  const busy = useRef(false);
  const lastInput = useRef(Date.now());
  const reloadRequested = useRef(false);
  navRef.current = nav;
  currentScreen = content ? (viewerOpen ? 'visor' : nav.name) : 'primer-arranque';

  const isIdle = nav.name === 'attract' && !viewerOpen && !admin;

  /* ---------- Conexión con el motor de sincronización ---------- */
  useEffect(() => {
    sync.onContent = (c) => setContent(c);
    sync.onCommand = (cmd) => {
      if (cmd === 'recargar') reloadRequested.current = true;
    };
    setContent(sync.content);
  }, [sync]);

  useEffect(() => {
    sync.setIdleProvider(() => navRef.current.name === 'attract' && !viewer.current);
  }, [sync]);

  /* ---------- Reposo: aplicar versión nueva, actualizar app, recarga preventiva ---------- */
  useEffect(() => {
    if (!isIdle || !content) return;
    void sync.applyPendingIfIdle();
    const t = setInterval(() => {
      void sync.applyPendingIfIdle();
      if (reloadRequested.current) location.reload();
      else if (hasAppUpdate()) applyAppUpdate();
      else if (uptimeHours() > CONFIG.softReloadHours) location.reload();
    }, 5000);
    return () => clearInterval(t);
  }, [isIdle, content, sync]);

  useEffect(() => {
    if (!content) return;
    const t = setTimeout(() => void preloadViewer().catch((e) => report(e, { op: 'preload-viewer' })), 3000);
    return () => clearTimeout(t);
  }, [content]);

  /* ---------- Navegación con la transición del diseño (220 ms) ---------- */
  const go = useCallback((next: Nav) => {
    if (busy.current) return;
    busy.current = true;
    const toHome = next.name !== 'gallery';
    setVis({ o: 0, y: toHome ? 40 : -40 });
    setTimeout(() => {
      setNav(next);
      setVis({ o: 1, y: 0 });
      busy.current = false;
    }, 230);
  }, []);

  /* ---------- Inactividad ---------- */
  useEffect(() => {
    const touch = () => {
      lastInput.current = Date.now();
    };
    const opts = { capture: true, passive: true };
    window.addEventListener('pointerdown', touch, opts);
    window.addEventListener('pointermove', touch, opts);
    window.addEventListener('keydown', touch, opts);
    window.addEventListener('wheel', touch, opts);
    return () => {
      window.removeEventListener('pointerdown', touch, opts);
      window.removeEventListener('pointermove', touch, opts);
      window.removeEventListener('keydown', touch, opts);
      window.removeEventListener('wheel', touch, opts);
    };
  }, []);

  const idleSeconds = content?.manifest.settings.segundosInactividad ?? CONFIG.defaultIdleSeconds;
  useEffect(() => {
    if (isIdle || prompt || admin) return;
    lastInput.current = Date.now();
    const t = setInterval(() => {
      if (Date.now() - lastInput.current > idleSeconds * 1000) setPrompt(true);
    }, 1000);
    return () => clearInterval(t);
  }, [isIdle, prompt, admin, idleSeconds]);

  const backToRest = useCallback(() => {
    setPrompt(false);
    viewer.current?.close();
    viewer.current = null;
    setViewerOpen(false);
    go({ name: 'attract' });
  }, [go]);

  /* ---------- Visor ---------- */
  const project = useMemo(
    () => (nav.name === 'gallery' && content ? content.manifest.projects.find((p) => p.id === nav.projectId) || null : null),
    [nav, content],
  );

  const openFull = useCallback(async () => {
    if (nav.name !== 'gallery' || !project || !content || viewer.current) return;
    const tab = nav.tab;
    const items = verItems(project, tab, nav.modelo);
    if (!items.length) return;
    const abierto = modeloAbierto(project, tab, nav.modelo);
    setViewerOpen(true);
    try {
      viewer.current = await openViewer({
        items,
        index: nav.idx,
        src: content.src,
        title: project.nombre,
        tabLabel: abierto ? abierto.nombre : TAB_LABEL[tab],
        label: (img, i) => imageLabel(tab, img, i),
        onChange: (i) => setNav((n) => (n.name === 'gallery' ? { ...n, idx: i } : n)),
        onClose: () => {
          viewer.current = null;
          setViewerOpen(false);
        },
      });
    } catch (e) {
      report(e, { op: 'open-viewer' });
      setViewerOpen(false);
    }
  }, [nav, project, content]);

  const ctx = useMemo<ContentCtx | null>(() => (content ? { content, reportMissing: (k) => sync.reportMissing(k) } : null), [content, sync]);

  const openProject = useCallback(
    (p: ProjectEntry) => {
      const tab = defaultTab(p);
      predecode([content?.src(tabItems(p, tab)[0]?.main)], true);
      go({ name: 'gallery', projectId: p.id, tab, idx: 0, modelo: null });
    },
    [go, content],
  );

  let screen: ReactNode;
  if (!ctx) {
    screen = <FirstBoot sync={sync} />;
  } else if (nav.name === 'attract') {
    screen = <Attract onStart={() => go({ name: 'home' })} />;
  } else if (nav.name === 'gallery' && project) {
    screen = (
      <Gallery
        project={project}
        tab={nav.tab}
        idx={nav.idx}
        modelo={nav.modelo}
        onTab={(tab) => setNav({ ...nav, tab, idx: 0, modelo: null })}
        onIdx={(idx) => setNav((n) => (n.name === 'gallery' ? { ...n, idx } : n))}
        onModelo={(modelo) => setNav((n) => (n.name === 'gallery' ? { ...n, modelo, idx: 0 } : n))}
        onBack={() => go({ name: 'home' })}
        onViewer={openFull}
      />
    );
  } else {
    screen = <Home onOpen={openProject} onAdmin={() => setAdmin(true)} />;
  }

  return (
    <Stage>
      <ErrorBoundary
        key={resetKey}
        onError={() => {
          setNav({ name: 'attract' });
          setResetKey((k) => k + 1);
        }}
      >
        {ctx ? (
          <ContentContext.Provider value={ctx}>
            <div
              className="fill"
              style={{ opacity: vis.o, transform: `translate3d(0, ${vis.y}px, 0)`, transition: 'opacity 220ms ease, transform 220ms ease' }}
            >
              {screen}
            </div>
          </ContentContext.Provider>
        ) : (
          screen
        )}
      </ErrorBoundary>
      {prompt ? (
        // Portal sobre <body>: debe quedar encima del visor PhotoSwipe, que también vive en <body>
        <Overlay>
          <IdlePrompt
            onStay={() => {
              lastInput.current = Date.now();
              setPrompt(false);
            }}
            onTimeout={backToRest}
          />
        </Overlay>
      ) : null}
      {admin ? <AdminPanel sync={sync} onClose={() => setAdmin(false)} /> : null}
    </Stage>
  );
}

/* ---------- Lienzo 1080×1920 escalado al viewport (en el tótem: escala 1) ---------- */
function useFit() {
  const [box, setBox] = useState(fit);
  useEffect(() => {
    const on = () => setBox(fit());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return box;
}

function Overlay({ children }: { children: ReactNode }) {
  const box = useFit();
  return createPortal(
    <div className="fixed" style={{ top: 0, left: 0, right: 0, bottom: 0, zIndex: 200000 }}>
      <div className="absolute" style={{ left: 0, top: 0, width: 1080, height: 1920, transformOrigin: '0 0', transform: `translate(${box.x}px, ${box.y}px) scale(${box.s})` }}>
        {children}
      </div>
    </div>,
    document.body,
  );
}

function Stage({ children }: { children: ReactNode }) {
  const box = useFit();
  return (
    <div className="fill overflow-hidden bg-escenario" onContextMenu={(e) => e.preventDefault()}>
      <div className="stage" style={{ transform: `translate(${box.x}px, ${box.y}px) scale(${box.s})` }}>
        {children}
      </div>
    </div>
  );
}

function fit() {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const s = Math.min(w / 1080, h / 1920);
  document.documentElement.style.setProperty('--stage-scale', String(s));
  return { s, x: (w - 1080 * s) / 2, y: (h - 1920 * s) / 2 };
}

/* ---------- Nunca pantalla en blanco: ante un error de render se vuelve al reposo ---------- */
class ErrorBoundary extends Component<{ children: ReactNode; onError: () => void }, { failed: boolean }> {
  static errors: number[] = [];
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: unknown) {
    report(error, { op: 'render' });
    const now = Date.now();
    ErrorBoundary.errors = [...ErrorBoundary.errors.filter((t) => t > now - 60_000), now];
    if (ErrorBoundary.errors.length >= 3) {
      location.reload();
      return;
    }
    setTimeout(this.props.onError, 50);
  }
  render() {
    return this.state.failed ? <div className="fill bg-primario" /> : this.props.children;
  }
}
